import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, FileText, Shield, ClipboardCheck, Phone, ArrowRight, Download } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import AppointmentModal from "../components/AppointmentModal";
import ReferralFormModal from "../components/ReferralFormModal";
import { downloadBlankIntakePacket } from "../utils/intakePacketPdf";

const options = [
  {
    icon: Calendar,
    title: "Schedule an Appointment",
    description: "Book your first visit with one of our licensed clinicians. We offer flexible scheduling including evening appointments.",
    action: "appointment",
    color: "bg-blue-600 hover:bg-blue-700",
    iconBg: "bg-blue-100 text-blue-600",
  },
  {
    icon: FileText,
    title: "Submit a Referral",
    description: "Providers and agencies can submit an OMHC or PRP referral using our secure online form.",
    action: "referral",
    color: "bg-indigo-600 hover:bg-indigo-700",
    iconBg: "bg-indigo-100 text-indigo-600",
  },
  {
    icon: Shield,
    title: "Check Insurance Coverage",
    description: "See which insurance plans we accept, including Medicaid, Medicare, and most major carriers.",
    action: "link",
    href: "/insurance",
    color: "bg-emerald-600 hover:bg-emerald-700",
    iconBg: "bg-emerald-100 text-emerald-600",
  },
  {
    icon: ClipboardCheck,
    title: "Complete Intake Forms",
    description: "Complete your intake and consent forms online before your first visit. Or download a printable packet to bring in person.",
    action: "intake",
    color: "bg-teal-600 hover:bg-teal-700",
    iconBg: "bg-teal-100 text-teal-600",
  },
  {
    icon: ClipboardCheck,
    title: "Take a Self-Assessment",
    description: "Complete a confidential screening for anxiety, depression, or ADHD to better understand your needs.",
    action: "link",
    href: "/self-assessment",
    color: "bg-purple-600 hover:bg-purple-700",
    iconBg: "bg-purple-100 text-purple-600",
  },
  {
    icon: Phone,
    title: "Call Us Directly",
    description: "Speak with our team by phone. We are available Monday through Friday, 9 AM to 5 PM.",
    action: "phone",
    color: "bg-green-600 hover:bg-green-700",
    iconBg: "bg-green-100 text-green-600",
  },
];

const GetStartedPage = () => {
  const [showAppointment, setShowAppointment] = useState(false);
  const [showReferral, setShowReferral] = useState(false);

  const handleAction = (option) => {
    if (option.action === "appointment") setShowAppointment(true);
    else if (option.action === "referral") setShowReferral(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Get Started"
        description="Begin your journey with SDORA Wellbeing. Schedule an appointment, submit a referral, check insurance coverage, take a self-assessment, or call us at (877) 772-5150."
        path="/get-started"
      />
      <Navigation />

      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Get Started with SDORA Wellbeing
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Whether you are seeking care for yourself, a family member, or making a professional referral, 
            choose whichever starting point fits your situation.
          </p>
        </div>
      </div>

      {/* Options */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-4">
          {options.map((opt) => {
            const Icon = opt.icon;
            const content = (
              <div
                className="flex items-start gap-5 p-6 bg-white border border-gray-200 rounded-xl hover:shadow-lg hover:border-gray-300 transition-all group"
                data-testid={`get-started-${opt.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`p-3 rounded-xl ${opt.iconBg} flex-shrink-0`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 mb-1">{opt.title}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed">{opt.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-700 flex-shrink-0 mt-1 transition-colors" />
              </div>
            );

            if (opt.action === "link") {
              return <Link key={opt.title} to={opt.href} className="block">{content}</Link>;
            }
            if (opt.action === "phone") {
              return <a key={opt.title} href="tel:+18777725150" className="block">{content}</a>;
            }
            if (opt.action === "intake") {
              return (
                <div key={opt.title} className="block">
                  <Link to="/intake" className="block">{content}</Link>
                  <div className="flex items-center gap-3 mt-2 ml-16 pl-2">
                    <Link to="/intake" className="text-xs text-teal-700 font-semibold hover:underline" data-testid="get-started-intake-online">
                      Complete Online →
                    </Link>
                    <span className="text-xs text-gray-400">or</span>
                    <button onClick={downloadBlankIntakePacket} className="text-xs text-gray-600 font-medium hover:text-gray-800 flex items-center gap-1" data-testid="get-started-download-packet">
                      <Download className="w-3 h-3" /> Download Printable Packet
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <button key={opt.title} onClick={() => handleAction(opt)} className="block w-full text-left">
                {content}
              </button>
            );
          })}
        </div>

        {/* Additional info */}
        <div className="mt-12 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-base font-bold text-gray-900 mb-2">Not sure where to start?</h3>
          <p className="text-sm text-gray-600 mb-3">
            Call us at <a href="tel:+18777725150" className="text-blue-600 font-semibold hover:underline">(877) 772-5150</a> and 
            our team will help you find the right service. You can also learn more about 
            our <Link to="/services/comprehensive-mental-health" className="text-blue-600 font-semibold hover:underline">full range of services</Link> or 
            meet <Link to="/team" className="text-blue-600 font-semibold hover:underline">our clinical team</Link>.
          </p>
          <p className="text-xs text-gray-500">
            SDORA Wellbeing LLC is CARF accredited and located at 1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208.
          </p>
        </div>
      </div>

      <Footer />

      {showAppointment && <AppointmentModal onClose={() => setShowAppointment(false)} />}
      {showReferral && <ReferralFormModal isOpen={showReferral} onClose={() => setShowReferral(false)} />}
    </div>
  );
};

export default GetStartedPage;
